import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/authStore';

const Icon = ({ d, size = 18 }: { d: string; size?: number }) => (
  <svg className="sidebar__item-icon" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg className="sidebar__item-chevron" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const ICONS = {
  dashboard: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  ticket:    'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z',
  asset:     'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18',
  payment:   'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  license:   'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
  users:     'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  reports:   'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  template:  'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  roles:     'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  settings:  'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
} as const;

const TICKET_SUBNAV = [
  { label: 'All Tickets',       to: '/tickets' },
  { label: 'New Tickets',       to: '/tickets?status=OPEN' },
  { label: 'Critical Priority', to: '/tickets?priority=CRITICAL' },
  { label: 'My Tickets',        to: '/tickets?assigned=me' },
  { label: 'Assigned to Me',    to: '/tickets?assigned=me' },
  { label: 'Ticket History',    to: '/tickets?status=CLOSED' },
];

// `view` must match the Assets page views; `perm` hides links the user can't use
const ASSET_SUBNAV: { label: string; to: string; view?: string; perm?: string }[] = [
  { label: 'Asset Dashboard',   to: '/assets/dashboard', perm: 'analytics:read' },
  { label: 'All Assets',        to: '/assets', view: '' },
  { label: 'In Use',            to: '/assets?view=in_use', view: 'in_use', perm: 'assets:update' },
  { label: 'In Stock',          to: '/assets?view=in_stock', view: 'in_stock', perm: 'assets:update' },
  { label: 'In Repair',         to: '/assets?view=in_repair', view: 'in_repair', perm: 'assets:update' },
  { label: 'Warranty Expiring', to: '/assets?view=warranty_expiring', view: 'warranty_expiring', perm: 'assets:update' },
  { label: 'Retired',           to: '/assets?view=retired', view: 'retired', perm: 'assets:update' },
  { label: 'Maintenance',       to: '/assets/maintenance', perm: 'assets:update' },
  { label: 'Asset Categories',  to: '/settings/asset-categories', perm: 'settings:update' },
];

const LICENSE_SUBNAV = [
  { label: 'All Licences',      to: '/licenses' },
  { label: 'Expiring Soon',     to: '/licenses?view=expiring' },
  { label: 'SaaS Contracts',    to: '/payments?tab=contracts&category=SAAS', perm: 'payments:read' },
];

// Work queues follow the payment flow: verify → approve → Accounts pays
const PAYMENT_SUBNAV = [
  { label: 'Overview',          to: '/payments' },
  { label: 'All Payments',      to: '/payments?tab=payments' },
  { label: 'To Verify',         to: '/payments?tab=payments&view=to_verify', perm: 'payments:update' },
  { label: 'My Approvals',      to: '/payments?tab=payments&view=to_approve', perm: 'payments:approve' },
  { label: 'Accounts — To Pay', to: '/payments?tab=payments&view=to_pay', perm: 'payments:process' },
  { label: 'Contracts',         to: '/payments?tab=contracts' },
  { label: 'Vendors',           to: '/payments?tab=vendors' },
];

const USERS_SUBNAV = [
  { label: 'All Users',    to: '/users' },
  { label: 'Super Admins', to: '/users?role=SUPER_ADMIN' },
  { label: 'IT Admins',    to: '/users?role=IT_ADMIN' },
  { label: 'Technicians',  to: '/users?role=TECHNICIAN' },
  { label: 'Managers',     to: '/users?role=MANAGER' },
  { label: 'End Users',    to: '/users?role=END_USER' },
];

export function Sidebar() {
  const perms    = useAuthStore(s => s.user?.permissions ?? []);
  const location = useLocation();
  const isTickets  = location.pathname.startsWith('/tickets');
  const isAssets   = location.pathname.startsWith('/assets') || location.pathname === '/settings/asset-categories';
  const isLicenses = location.pathname.startsWith('/licenses');
  const isPayments = location.pathname.startsWith('/payments');
  const isUsers    = location.pathname.startsWith('/users');

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    tickets:  isTickets,
    assets:   isAssets,
    licenses: isLicenses,
    payments: isPayments,
    users:    isUsers,
  });

  const toggle = (key: string) =>
    setExpanded(e => ({ ...e, [key]: !e[key] }));

  const can = (p: string) => perms.includes(p);

  return (
    <aside className="sidebar layout__sidebar">
      {/* Logo */}
      <NavLink to="/dashboard" className="sidebar__logo" aria-label="Home">
        <img
          src="/aneralogo.png?v=2"
          alt="Anera"
          style={{ height: 32, width: 'auto', maxWidth: 148, objectFit: 'contain' }}
        />
      </NavLink>

      <nav className="sidebar__nav">
        {/* Dashboard — admin/analytics users only */}
        {can('analytics:read') && (
          <NavLink to="/dashboard"
            className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--active' : ''}`}>
            <Icon d={ICONS.dashboard} />
            <span className="sidebar__item-label">Dashboard</span>
          </NavLink>
        )}

        {/* Tickets — expandable */}
        {can('tickets:read') && (
          <>
            <button
              type="button"
              onClick={() => toggle('tickets')}
              className={`sidebar__item${isTickets ? ' sidebar__item--active' : ''}`}
              style={{ border: 'none', cursor: 'pointer' }}
            >
              <Icon d={ICONS.ticket} />
              <span className="sidebar__item-label">Tickets</span>
              <ChevronDown open={!!expanded.tickets} />
            </button>
            {expanded.tickets && (
              <div className="sidebar__subnav">
                {TICKET_SUBNAV.map((s, i) => (
                  <NavLink key={i} to={s.to}
                    className={({ isActive }) =>
                      `sidebar__subitem${isActive && s.to === '/tickets' ? ' sidebar__subitem--active' : ''}`
                    }>
                    {s.label}
                  </NavLink>
                ))}
              </div>
            )}
          </>
        )}

        {/* Assets — expandable */}
        {can('assets:read') && (
          <>
            <button
              type="button"
              onClick={() => toggle('assets')}
              className={`sidebar__item${isAssets ? ' sidebar__item--active' : ''}`}
              style={{ border: 'none', cursor: 'pointer' }}
            >
              <Icon d={ICONS.asset} />
              <span className="sidebar__item-label">Assets</span>
              <ChevronDown open={!!expanded.assets} />
            </button>
            {expanded.assets && (
              <div className="sidebar__subnav">
                {ASSET_SUBNAV.filter(s => !s.perm || can(s.perm)).map(s => {
                  const currentView = new URLSearchParams(location.search).get('view') ?? '';
                  const active = s.view !== undefined
                    ? location.pathname === '/assets' && currentView === s.view
                    : location.pathname === s.to;
                  return (
                    <NavLink key={s.to} to={s.to}
                      className={`sidebar__subitem${active ? ' sidebar__subitem--active' : ''}`}>
                      {s.view === '' && !can('assets:update') && !can('assets:assign') ? 'My Assets' : s.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Payments & contracts — expandable */}
        {can('payments:read') && (
          <>
            <button
              type="button"
              onClick={() => toggle('payments')}
              className={`sidebar__item${isPayments ? ' sidebar__item--active' : ''}`}
              style={{ border: 'none', cursor: 'pointer' }}
            >
              <Icon d={ICONS.payment} />
              <span className="sidebar__item-label">Payments</span>
              <ChevronDown open={!!expanded.payments} />
            </button>
            {expanded.payments && (
              <div className="sidebar__subnav">
                {PAYMENT_SUBNAV.filter(sub => !sub.perm || can(sub.perm)).map((sub, i) => (
                  <NavLink key={i} to={sub.to}
                    className={() => `sidebar__subitem${location.pathname + location.search === sub.to ? ' sidebar__subitem--active' : ''}`}>
                    {sub.label}
                  </NavLink>
                ))}
              </div>
            )}
          </>
        )}

        {/* Licenses — expandable */}
        {can('licenses:read') && (
          <>
            <button
              type="button"
              onClick={() => toggle('licenses')}
              className={`sidebar__item${isLicenses ? ' sidebar__item--active' : ''}`}
              style={{ border: 'none', cursor: 'pointer' }}
            >
              <Icon d={ICONS.license} />
              <span className="sidebar__item-label">Licenses</span>
              <ChevronDown open={!!expanded.licenses} />
            </button>
            {expanded.licenses && (
              <div className="sidebar__subnav">
                {LICENSE_SUBNAV.filter(sub => !sub.perm || can(sub.perm)).map((sub, i) => (
                  <NavLink key={i} to={sub.to}
                    className={() => `sidebar__subitem${location.pathname + location.search === sub.to ? ' sidebar__subitem--active' : ''}`}>
                    {sub.label}
                  </NavLink>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Admin section ── */}
        {(can('users:read') || can('analytics:read') || can('templates:read')) && (
          <div className="sidebar__divider" />
        )}

        {/* Users — expandable */}
        {can('users:read') && (
          <>
            <button
              type="button"
              onClick={() => toggle('users')}
              className={`sidebar__item${isUsers ? ' sidebar__item--active' : ''}`}
              style={{ border: 'none', cursor: 'pointer' }}
            >
              <Icon d={ICONS.users} />
              <span className="sidebar__item-label">Users</span>
              <ChevronDown open={!!expanded.users} />
            </button>
            {expanded.users && (
              <div className="sidebar__subnav">
                {USERS_SUBNAV.map((s, i) => {
                  const urlRole = s.to.includes('?role=') ? s.to.split('?role=')[1] : '';
                  const currentRole = new URLSearchParams(location.search).get('role') ?? '';
                  const isSubActive = urlRole
                    ? currentRole.toUpperCase() === urlRole.toUpperCase()
                    : location.pathname === '/users' && !currentRole;
                  return (
                    <NavLink key={i} to={s.to}
                      className={`sidebar__subitem${isSubActive ? ' sidebar__subitem--active' : ''}`}>
                      {s.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </>
        )}


        {/* Roles & Permissions */}
        {can('roles:read') && (
          <NavLink to="/roles"
            className={({ isActive }) => `sidebar__item${isActive ? " sidebar__item--active" : ""}`}>
            <Icon d={ICONS.roles} />
            <span className="sidebar__item-label">Roles & Permissions</span>
          </NavLink>
        )}
        {can('analytics:read') && (
          <NavLink to="/analytics"
            className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--active' : ''}`}>
            <Icon d={ICONS.reports} />
            <span className="sidebar__item-label">Reports</span>
          </NavLink>
        )}

        {can('templates:read') && (
          <NavLink to="/templates"
            className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--active' : ''}`}>
            <Icon d={ICONS.template} />
            <span className="sidebar__item-label">Templates</span>
          </NavLink>
        )}

        {/* Settings — admin users only */}
        {can('settings:read') && (
          <>
            <div className="sidebar__divider" style={{ marginTop: 'auto' }} />
            <NavLink to="/settings"
              className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--active' : ''}`}>
              <Icon d={ICONS.settings} />
              <span className="sidebar__item-label">Settings</span>
            </NavLink>
          </>
        )}
      </nav>
    </aside>
  );
}
